import React from "react";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";

import {
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@material-ui/core";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts";

const styles = theme => ({
  pageName: {
    fontWeight: "bold"
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  measurementGraph: {
    padding: theme.spacing(2)
  },
  lastMeasurementPaper: {
    padding: theme.spacing(2),
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column"
  },
  lastMeasurementContainer: {
    padding: theme.spacing(4),
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },
  lastMeasurementTitle: {
    marginBottom: theme.spacing(2)
  },
  lastMeasurementValue: {
    marginBottom: -theme.spacing(1)
  }
});

class SensorInfo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      table: {
        rowsPerPage: 5,
        currentPage: 0
      }
    };
  }

  setRowsPerPage(rowsPerPage) {
    this.setState(state => ({
      ...state,
      table: {
        ...state.table,
        rowsPerPage: rowsPerPage
      }
    }));
  }

  setCurrentPage(currentPage) {
    this.setState(state => ({
      ...state,
      table: {
        ...state.table,
        currentPage: currentPage
      }
    }));
  }

  componentDidMount() {
    const sensorStore = this.props.rootStore.sensorStore;

    this.setState(state => ({
      ...state,
      loading: true
    }));

    sensorStore.fetchAndUpdate().then(() => {
      const sensor = sensorStore.sensors.get(this.props.id);
      sensor.fetchAndUpdateMeasurements().then(() => {
        this.setState(state => ({
          ...state,
          loading: false
        }));
      });
    });
  }

  render() {
    const classes = this.props.classes;

    if (this.state.loading) {
      return (
        <div className={classes.progressContainer}>
          <CircularProgress className={classes.progress} />
        </div>
      );
    }

    const sensor =
      this.props.rootStore.sensorStore.sensors.get(this.props.id) || {};
    const measurements = sensor.measurements || [];

    const data = measurements
      .slice()
      .reverse()
      .map(
        m =>
          new Map(
            Object.entries({
              id: m.id,
              timestamp: Number(moment(m.timestamp).valueOf()),
              ppm: Number(m.payload.ppm.toFixed(1))
            })
          )
      )
      .map(m => {
        if (this.props.user.is_staff) {
          return m;
        } else {
          return m.set("ppm", Math.max(m.get("ppm"), 0.1));
        }
      });

    const renderLineChart = (
      <ResponsiveContainer aspect={2.7} width={"100%"}>
        <LineChart data={data}>
          <XAxis
            dataKey={measurement => measurement.get("timestamp")}
            domain={["dataMin", "dataMax"]}
            type="number"
            tickFormatter={tick => moment(tick).format("l h:mma")}
          />
          <YAxis
            dataKey={measurement => measurement.get("ppm")}
            type="number"
            domain={[0, 5]}
          />
          <Line
            type="monotone"
            stroke="#adc537"
            strokeWidth={4}
            dataKey={measurement => measurement.get("ppm")}
            fillOpacity={0.5}
            dot={false}
          />
          <CartesianGrid strokeDasharray="3 3" />
        </LineChart>
      </ResponsiveContainer>
    );

    const tableStart =
      this.state.table.currentPage + this.state.table.rowsPerPage;
    const renderTable = (
      <React.Fragment>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>PPM</TableCell>
              <TableCell align="right">Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice()
              .reverse()
              .slice(tableStart, tableStart + this.state.table.rowsPerPage)
              .map(measurement => (
                <TableRow key={measurement.get("id")}>
                  <TableCell component="th" scope="row">
                    {measurement.get("ppm")}
                  </TableCell>
                  <TableCell align="right">
                    {moment(measurement.get("timestamp")).format("L hh:mm:ssa")}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={this.state.table.rowsPerPage}
          page={this.state.table.currentPage}
          backIconButtonProps={{
            "aria-label": "Previous Page"
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page"
          }}
          onChangePage={(e, newPage) => this.setCurrentPage(newPage)}
          onChangeRowsPerPage={e => this.setRowsPerPage(e.target.value)}
        />
      </React.Fragment>
    );

    const lastMeasurement = data.length ? data[data.length - 1].get("ppm") : 0;

    return (
      <React.Fragment>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" className={classes.pageName}>
              {sensor.human_name || ""}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper className={classes.lastMeasurementPaper}>
              <Typography variant="h6" className={classes.lastMeasurementTitle}>
                Last Measurement
              </Typography>
              <Divider />
              <div className={classes.lastMeasurementContainer}>
                <Typography
                  variant="h2"
                  className={classes.lastMeasurementValue}
                >
                  {lastMeasurement}
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.lastMeasurementUnits}
                >
                  PPM
                </Typography>
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper className={classes.measurementGraph}>
              {renderLineChart}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.sensorMeasurements}>{renderTable}</Paper>
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(
  inject(stores => ({
    user: stores.rootStore.authStore.user,
    rootStore: stores.rootStore
  }))(observer(SensorInfo))
);
